import ExternalLink from '@src/app-components/external-link/ExternalLink';
import { Alert } from '@trussworks/react-uswds';
import '../../resources.scss';
import { RequestType } from '@src/utils/enums';

// Static Content Tooltip Content

export const standardPermitTooltipContent = (
  <>
    There are two types of IPs- Letters of Permission and Standard Permits (SP). SPs are the only type that requires a
    public notice to advertise the proposed work to the regulated public.
  </>
);

export const generalPermitTooltipContent = (
  <>
    A more streamlined review when the project meets certain criteria – consists of Nationwide Permits, Regional General
    Permits, and Programmatic General Permits.
  </>
);

export const permitTooltipContent = (
  <>
    <p>
      This Request Type should be used for All Permit Types (SP, LOP, NWP, RGP, PGP). Request an Application for
      Department of the Army (DA) Permit when applying for an individual or standard permit. This request path may also
      be used to request modification of an existing permit. An individual or standard permit is issued when projects
      have more than minimal individual or cumulative impacts, are evaluated using additional environmental criteria,
      and involve a more comprehensive public interest review.
    </p>
    <p>
      This form can also be used to receive confirmation that a particular activity is authorized by a USACE general
      permit, or to request re-verification of a prior cycle Nationwide or other general permit. Additional information
      will be asked during the evaluation process to ensure thresholds are not exceeded. The three types of general
      permits are Nationwide Permits, Regional General Permits and Programmatic General Permits. General permits are
      usually valid for five years and issued on a nationwide, regional, or state basis for activities with minimal
      individual or cumulative adverse environmental effects.
    </p>
  </>
);

export const existingProjectTooltipContent = (
  <>
    Choose an existing RRS project to reuse its location and general information. You can type to search by name or DA
    number, or select one from the list below. Currently, only projects created in RRS can be referenced. The ability to
    reference additional regulatory projects may be added in the future.
  </>
);

export const projectDescriptionTooltipContent = (
  <>
    Describe the proposed activity, including the direct and indirect adverse environmental effects the activity would
    cause, its purpose, and its intended use. The description of the proposed activity should also include information
    on how the activity has been designed to avoid and minimize impacts to WOTUS to the maximum extent practicable.
    Identify the material(s) to be used in construction, as well as the methods by which the work is to be done.
  </>
);

export const projectPurposeTooltipContent = (
  <>
    Describe the purpose and need for the proposed project. What will it be used for and why? Also include a brief
    description of any related activities to be developed as the result of the proposed project.
  </>
);

export const natureOfActivityTooltipContent = (
  <>
    Describe the overall activity or project. Give appropriate dimensions of structures, fills and/or excavation
    (length, width, and depth). If the project would include the construction of a filled or pile or float-supported
    platform, you must include a description of the proposed use including any structures to be erected on the fill or
    platform.
  </>
);

export const noPermitRequiredTooltipContent = (
  <>
    Request an {RequestType.ActivityBasedNoPermitRequired} letter when seeking documentation that a particular activity
    does not require authorization from USACE. This path does not address geographic jurisdiction of aquatic resources;
    an official USACE determination that jurisdictional "waters of the United States," or "navigable waters of the
    United States" are either present or absent on the project site should be undertaken by submitting a Request for
    Jurisdictional Determination (JD) / Aquatic Resource Delineation through the Regulatory Request System.
  </>
);

export const nonPcnTooltipContent = (
  <>
    <p>
      This optional request path focuses on ensuring activities do not require a pre-construction notification. If you
      need to submit a Pre-construction Notification (PCN), use the Department of the Army (DA) Permit Application
      request path of RRS.
    </p>
    <p>
      For some NWPs, applicants can proceed with the authorized activities without notifying the USACE district offices
      of those activities as long as the applicant complies with all applicable terms and conditions of those NWPs.
      Other NWPs require project proponents to submit preconstruction notifications (PCNs) to USACE districts prior to
      proceeding with the authorized activities to give district engineers the opportunity to determine whether the
      applicants' proposed activities are authorized by an NWP. The former set of NWPs are called non notifying NWPs and
      the latter set of NWPs are called PCN NWPs.
    </p>
    <Alert type='info' noIcon slim>
      NWPs 3b, 7, 8, 17, 21, 27, 29, 31, 34, 37, 38, 39, 40, 42, 44, 45, 46, 49, 50, 52, 53, 54, 55, 59 require a PCN
      for all activities. Go to the{' '}
      <span>
        <ExternalLink
          href='https://www.usace.army.mil/Missions/Civil-Works/Regulatory-Program-and-Permits/Nationwide-Permits/'
          title='HQ Website'
          content='HQ Website'
        />
      </span>{' '}
      for explanation of all NWPs.
    </Alert>
  </>
);

export const wotusDelineationReportTooltipContent = (
  <>
    <p>
      Select this request type when you would like to submit an Aquatic Resource Delineation Report and/or Aquatic
      Resource Inventory associated with another request type.
    </p>
    <p>
      An Aquatic Resource Delineation Report is a document or series of documents that describe the methodology used to
      conduct the delineation and typically includes a collection of one or more maps, datasheets, photographs, and/or
      figures that depict and support a legally defensible delineation of waters of the United States on a property.
    </p>
    <p>
      An Aquatic Resource Inventory is an accounting of all aquatic resources, including all waters (streams, lakes,
      ponds, etc.) and wetlands, within the project site. Information provided will include the type, geometry, and
      measurements of these resources in the project area.
    </p>
  </>
);

export const jurisdictionalDeterminationTooltipContent = (
  <>
    Request a Jurisdictional Determination (JD) when you would like USACE to confirm whether aquatic resources are under
    USACE authority to regulate. A JD is a written determination by the USACE identifying which delineated areas are or
    may be (depending on the type of JD requested) regulated by the USACE under Section 404 of the Clean Water Act
    and/or Section 10 of the Rivers and Harbors Act.
  </>
);

export const eng6082TooltipContent = (
  <>
    <p>
      Submit a Pre-Construction Notification (PCN) for confirmation that a particular activity is authorized by a USACE
      general permit. A PCN includes information about the proposed work and its anticipated environmental effects and
      may be required by the terms and conditions of a general permit, or by its regional conditions. This request path
      may also be used to request reverification of a prior cycle Nationwide or other general permit.
    </p>
    <p>
      A general permit is issued for structures, work or discharges that will result in only minimal adverse effects.
      General permits are issued on a nationwide, regional, or state basis for particular categories of activities.
      There are three types of general permits - Nationwide Permits, Regional General Permits, and Programmatic General
      Permits. General permits are usually valid for five years and may be re-authorized by USACE. This form focuses on
      ensuring certain thresholds are not exceeded which would result in more than minimal individual or cumulative
      adverse environmental effects.
    </p>
  </>
);
export const eng4345TooltipContent = (
  <>
    Request an Application for Department of the Army (DA) Permit when applying for an individual or standard permit.
    This request path may also be used to request modification of an existing permit. An individual or standard permit
    is issued when projects have more than minimal individual or cumulative impacts, are evaluated using additional
    environmental criteria, and involve a more comprehensive public interest review. This form may also be used to apply
    for general permits, but additional information may be asked during the evaluation process to ensure thresholds are
    not exceeded.
  </>
);
export const preAppTooltipContent = (
  <>
    Select this request type when you are unsure what type of permit you need or what information needs to be submitted
    for a permit application, if you wish to discuss proposed project details prior to submitting a permit, or if you
    need to submit a Project Scoping letter.
  </>
);
